import React, { ComponentType, ReactNode, Suspense } from 'react';
import { Flex, Spinner } from 'core/components';
import Navbar from 'app/components/navbar/Navbar';

type ImportComponentFn<P> = () => Promise<{ default: ComponentType<P> }>;
interface LazyLoadOptions {
  enablePageNav?: boolean;
  fallback?: ReactNode;
}
interface RetryOrReloadOptions {
  retriesLeft?: number;
  interval?: number;
}

const createFallback = ({ enablePageNav = false }) => (
  <>
    {enablePageNav && <Navbar bg="navbarBackground" onMenuToggle={() => {}} />}
    <Flex flex="1 1 100%" alignItems="center" justifyContent="center">
      <Spinner />
    </Flex>
  </>
);

/**
 * Returns a sessionStorage key that uniquely identifies a chunk load attempt.
 *
 * Prefer a chunk-specific key (chunk number or request URL) so that multiple
 * chunks loading on the same pathname don't share a key and interfere with
 * each other's reload-once guard.  Falls back to the pathname when no
 * chunk-specific info is available (e.g. generic errors that exhaust retries).
 */
function getReloadKey(error?: Error & { request?: string }): string {
  // Webpack's ChunkLoadError attaches the chunk's request URL (most unique).
  if (error?.request) {
    return `chunkRefreshed_request_${error.request}`;
  }
  // Fall back to the chunk number embedded in the error message.
  const chunkMatch = /Loading chunk (\d+) failed/.exec(error?.message ?? '');
  if (chunkMatch) {
    return `chunkRefreshed_chunk_${chunkMatch[1]}`;
  }
  return `chunkRefreshed_path_${window.location.pathname}`;
}

function retryOrReload<P extends object>(
  fn: ImportComponentFn<P>,
  options: RetryOrReloadOptions = {}
): Promise<{ default: ComponentType<P> }> {
  const { retriesLeft = 1, interval = 1000 } = options;

  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 0) {
            const key = getReloadKey(error);
            if (window.sessionStorage && !window.sessionStorage.getItem(key)) {
              window.sessionStorage.setItem(key, 'true');
              console.info('Retries exhausted, reloading to fetch updated assets:', key);
              // @ts-expect-error passing true to force reloads is only supported in firefox, I guess TS considers it a hack
              window.location.reload(true);
            } else {
              reject(error);
            }
          } else {
            console.info('Retrying lazy load, attempts remaining:', retriesLeft - 1);
            retryOrReload(fn, { retriesLeft: retriesLeft - 1, interval }).then(resolve, reject);
          }
        }, interval);
      });
  });
}

export default function lazyLoad<P extends object>(
  fn: ImportComponentFn<P>,
  { enablePageNav = false, fallback = null }: LazyLoadOptions = {}
) {
  const Component = React.lazy<ComponentType<P>>(() => retryOrReload(fn));

  return (props: P) => (
    <Suspense fallback={React.isValidElement(fallback) ? fallback : createFallback({ enablePageNav })}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <Component {...(props as any)} />
    </Suspense>
  );
}
